import { useState } from 'react';
import type { Project } from '../../shared/types';
import { api } from '../lib/api';
import { useStore } from '../lib/store';
import { formatINR, PROJECT_COLORS as SWATCHES } from '../lib/format';
import { PageHeader } from '../components/AppShell';
import { EmptyState, ErrorBanner, Modal } from '../components/ui';

interface Draft {
  name: string;
  client: string;
  color: string;
  rate: string;
}

const toDraft = (p?: Project): Draft => ({
  name: p?.name ?? '',
  client: p?.client ?? '',
  color: p?.color ?? SWATCHES[0]!,
  rate: p?.rate_inr != null ? String(p.rate_inr) : '',
});

export function Projects() {
  const { projects, settings, reloadProjects } = useStore();
  const [editing, setEditing] = useState<Project | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Draft>(toDraft());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = (project?: Project) => {
    setDraft(toDraft(project));
    setEditing(project ?? null);
    setCreating(!project);
    setError(null);
  };

  const close = () => {
    setEditing(null);
    setCreating(false);
  };

  const save = async () => {
    const rate = draft.rate.trim();
    if (rate && (!Number.isFinite(Number(rate)) || Number(rate) < 0)) {
      setError('Rate must be a non-negative number');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const input = {
        name: draft.name.trim(),
        client: draft.client.trim() || null,
        color: draft.color,
        // Blank means "inherit the account default", which is null, not zero.
        rate_inr: rate === '' ? null : Number(rate),
      };
      if (editing) await api.updateProject(editing.id, input);
      else await api.createProject(input);
      await reloadProjects();
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the project');
    } finally {
      setBusy(false);
    }
  };

  const act = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
      await reloadProjects();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    }
  };

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="Each project carries its own hourly rate in INR."
        actions={
          <button type="button" className="btn btn-primary" onClick={() => open()}>
            New project
          </button>
        }
      />

      {error && !creating && !editing && (
        <div className="mb-4">
          <ErrorBanner message={error} onDismiss={() => setError(null)} />
        </div>
      )}

      {projects.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No projects yet"
            description="Add the work you bill for. You can set a rate per project, or leave it blank to use the account default."
            action={
              <button type="button" className="btn btn-primary" onClick={() => open()}>
                Add your first project
              </button>
            }
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => {
            const inherited = project.rate_inr == null;
            return (
              <article
                key={project.id}
                className={`card px-5 py-4 ${project.archived ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start gap-2.5">
                  <span
                    className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: project.color }}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <h2 className="truncate text-[16px]">{project.name}</h2>
                    <p className="truncate text-[12px] text-[var(--color-ink-muted)]">
                      {project.client || 'No client'}
                      {project.archived ? ' · Archived' : ''}
                    </p>
                  </div>
                </div>

                <p className="nums mt-3 font-display text-[19px] font-semibold">
                  {formatINR(project.rate_inr ?? settings.default_rate_inr)}
                  <span className="ml-1 font-sans text-[12px] font-medium text-[var(--color-ink-muted)]">
                    / hour
                  </span>
                </p>
                {inherited && (
                  <p className="mt-0.5 text-[11px] text-[var(--color-ink-muted)]">
                    Uses the account default
                  </p>
                )}

                <div className="mt-4 flex flex-wrap gap-1.5 border-t border-[var(--color-line)] pt-3">
                  <button type="button" className="btn btn-ghost !py-1 text-[12px]" onClick={() => open(project)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost !py-1 text-[12px]"
                    onClick={() => void act(() => api.updateProject(project.id, { archived: project.archived ? 0 : 1 }))}
                  >
                    {project.archived ? 'Restore' : 'Archive'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost !py-1 text-[12px] !text-[var(--color-coral-600)] ml-auto"
                    onClick={() => {
                      if (
                        confirm(
                          `Delete "${project.name}"?\n\nEvery timesheet entry logged against it will be deleted too. This cannot be undone.`,
                        )
                      ) {
                        void act(() => api.deleteProject(project.id));
                      }
                    }}
                  >
                    Delete
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {(creating || editing) && (
        <Modal
          title={editing ? 'Edit project' : 'New project'}
          onClose={close}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={close}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void save()}
                disabled={busy || !draft.name.trim()}
              >
                {busy ? 'Saving…' : 'Save project'}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

            <div>
              <label className="label" htmlFor="p-name">
                Project name
              </label>
              <input
                id="p-name"
                className="field"
                autoFocus
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>

            <div>
              <label className="label" htmlFor="p-client">
                Client <span className="normal-case font-normal">(optional)</span>
              </label>
              <input
                id="p-client"
                className="field"
                value={draft.client}
                onChange={(e) => setDraft({ ...draft, client: e.target.value })}
              />
            </div>

            <div>
              <label className="label" htmlFor="p-rate">
                Rate per hour (INR)
              </label>
              <input
                id="p-rate"
                className="field nums"
                inputMode="decimal"
                placeholder={`Blank = account default (${formatINR(settings.default_rate_inr)})`}
                value={draft.rate}
                onChange={(e) => setDraft({ ...draft, rate: e.target.value })}
              />
            </div>

            <div>
              <span className="label">Colour</span>
              <div className="flex flex-wrap gap-2">
                {SWATCHES.map((color) => (
                  <button
                    key={color}
                    type="button"
                    aria-label={`Colour ${color}`}
                    aria-pressed={draft.color === color}
                    onClick={() => setDraft({ ...draft, color })}
                    className={`h-8 w-8 rounded-full transition-transform ${
                      draft.color === color
                        ? 'ring-2 ring-offset-2 ring-[var(--color-ink)] scale-105'
                        : 'hover:scale-105'
                    }`}
                    style={{ background: color }}
                  />
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
